// utils.ts
import { computed } from "vue";
import { useDisplay } from "vuetify";
import { useAppConfigStore } from "@/store/AppConfigStore";

import { AppConfigs } from "@/app.configs";
import { AppsData } from "@/constants/AppsData";
import { Constants } from "@/constants/Constants";
import { LocalStorageAuthTokenNotFoundError } from "@/exceptions/LocalStorageAuthTokenNotFoundError";
import { LocalStorageFirebaseAuthTokenNotFoundError } from "@/exceptions/LocalStorageFirebaseAuthTokenNotFoundError";
import { format } from 'date-fns';

export function isMobile() {
	const { name } = useDisplay();

	return computed(() => {
		const mobileSizes = ["xs"];
		const currentSize = name.value;
		const isMobile = mobileSizes.includes(currentSize);
		return isMobile;
	});
}

export function isTablet() {
	const { name } = useDisplay();

	return computed(() => {
		const tabletSizes = ["sm"];
		const currentSize = name.value;
		const isTablet = tabletSizes.includes(currentSize);
		return isTablet;
	});
}

export function isDesktop() {
	const { name } = useDisplay();

	return computed(() => {
		const desktopSizes = ["md", "lg", "xl", "xxl"];
		const currentSize = name.value;
		const isDesktop = desktopSizes.includes(currentSize);
		return isDesktop;
	});
}

export function isExtraLargeDesktop() {
	const { name } = useDisplay();

	return computed(() => {
		const desktopSizes = ["xl", "xxl"];
		const currentSize = name.value;
		const isExtraLargeDesktop = desktopSizes.includes(currentSize);
		return isExtraLargeDesktop;
	});
}


export function formatDate(timestamp:number) {
    // Create the Date object
	const date = new Date(timestamp * 1000);
	// Format the date using date-fns
	try {
		return format(date, 'MMMM dd, yyyy');
	} 
	catch (error) {
		return 'Invalid Date'; 
	}
  }

export function getCategoryName(id: number): string {
	const appConfigStore = useAppConfigStore();
	const category = appConfigStore.categories.find((category) => category.id === id);
	return category ? category.title : "";
}

export function getAuthTokenFromLocalStorage(): string | null {
	const authToken = localStorage.getItem(Constants.AUTHTOKEN);
	if (!authToken) {
		return null;
	}
	return authToken;
}

export function setAuthTokenInLocalStorage(authToken: string) {
	localStorage.setItem(Constants.AUTHTOKEN, authToken);
}

export function setFirebaseAuthTokenInLocalStorage(firebaseAuthToken: string) {
	localStorage.setItem(Constants.FIREBASEAUTHTOKEN, firebaseAuthToken);
}

export function getFirebaseAuthTokenFromLocalStorage(): string | null {
	const firebaseAuthToken = localStorage.getItem(Constants.FIREBASEAUTHTOKEN);
	if (!firebaseAuthToken) {
		return null;
	}
	return firebaseAuthToken;
}


export function removeAuthTokenFromLocalStorage() {
	localStorage.removeItem(Constants.AUTHTOKEN);
}

export function removeFirebaseAuthTokenFromLocalStorage() {
	localStorage.removeItem(Constants.FIREBASEAUTHTOKEN);
}


// scrolltotop
export function scrollToTop() {
	window.scrollTo({
		top: 0,
		behavior: "smooth",
	});
}

// redirecttoplaystore
export function redirectToPlayStore() {
	const appName = AppConfigs.appName;
	// Check if the app name is valid
	if (appName in AppsData) {
		const playStoreLink = AppsData[appName].playStoreLink;
		// Navigate to the playstore
		window.open(playStoreLink, "_blank");
	} else {
		console.log("Invalid app name ", appName);
	}
}

export async function CopyToClipboard(text: string) {
	await navigator.clipboard.writeText(text);
}

export function groupItemsByDate(data: object): Map<number, object> {
	if (!Array.isArray(data)) return new Map();

	return data.reduce((sectionMap, item) => {
		const date = new Date(item.created_at * 1000);
		date.setHours(0, 0, 0, 0);
		const time = date.getTime();

		const items = sectionMap.get(time) || [];
		items.push(item);

		sectionMap.set(time, items);
		return sectionMap;
	}, new Map());
}

export function sortMapEntriesDescending(map: Map<number, object>): Array<[number, object]> {
	return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
}


export function getReferralCodeFromSessionStorage(): string | null {
	return window.sessionStorage.getItem("referralCode");
}


export function setReferralCodeInSessionStorage(referralCode: string) {
	window.sessionStorage.setItem("referralCode", referralCode);
}


export function removeReferralCodeFromSessionStorage() {
	window.sessionStorage.removeItem("referralCode");
}



export function baseURL() {
	return window.location.origin;
}



export function getCurrencySymbol(currencyCode: string): string {
  const currencySymbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'AUD': '$',
    'CAD': '$',
    'JPY': '¥',
    'CNY': '¥',
    'INR': '₹',
    'RUB': '₽',
  };
  return currencySymbols[currencyCode as keyof typeof currencySymbols] || currencyCode;
}


/**
 * Formats a large number to use K (thousands) or M (millions) suffix on mobile devices
 * @param amount - The number to format
 * @returns Formatted number as string (e.g., "1.5K" or "1M")
 */
export const formatLargeNumberForMobile = (amount: number): string => {
	const isMobileDevice = isMobile();
	if (!isMobileDevice.value) return amount.toString();

	if (amount >= 1000000) {
		return Math.round(amount / 1000000) + 'M';
	} else if (amount >= 1000) {
		return Math.round(amount / 1000) + 'K';
	}
	return amount.toString();
};



export const formatPhoneNumber = (phoneNumber: string): string => {
	if (!phoneNumber) return '';
  
	// Example: Format the phone number as (123) 456-7890
	const cleaned = ('' + phoneNumber).replace(/\D/g, '');
	const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
	if (match) {
	return `(${match[1]}) ${match[2]}-${match[3]}`;
	}
	return phoneNumber;
  };

export function isUserLoggedIn(): boolean {
  const authToken = getAuthTokenFromLocalStorage();
  return Boolean(authToken && authToken.length > 0);
}
